// WhatsAppOptInBanner.tsx
// One-time dismissible banner shown to users who haven't set up WhatsApp notifications.
// Dismissed state is stored in localStorage so it doesn't reappear after the user closes it.
// Shown for a max of 3 sessions, then auto-stops (so it doesn't become annoying).

import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/joy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';

const DISMISSED_KEY = 'shreemastro_wa_banner_dismissed';
const SESSION_COUNT_KEY = 'shreemastro_wa_banner_sessions';
const MAX_SESSIONS = 3;

interface Props {
  userId: string;
  /** Whether the user has already opted in (banner won't show if true) */
  hasOptedIn: boolean;
  /** Callback when user clicks "Enable" — opens the modal */
  onEnable: () => void;
}

export default function WhatsAppOptInBanner({ userId, hasOptedIn, onEnable }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (hasOptedIn) return; // Already opted in — never show

    const dismissedForUser = localStorage.getItem(`${DISMISSED_KEY}_${userId}`);
    if (dismissedForUser) return; // User dismissed permanently

    // Track how many sessions this has been shown
    const sessionKey = `${SESSION_COUNT_KEY}_${userId}`;
    const count = parseInt(localStorage.getItem(sessionKey) ?? '0', 10);
    if (count >= MAX_SESSIONS) return; // Shown too many times

    // Increment session count
    localStorage.setItem(sessionKey, String(count + 1));

    // Small delay so it doesn't flash on initial render
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [userId, hasOptedIn]);

  function handleDismiss() {
    setVisible(false);
    localStorage.setItem(`${DISMISSED_KEY}_${userId}`, '1');
  }

  function handleEnable() {
    setVisible(false);
    onEnable();
  }

  if (!visible) return null;

  return (
    <Box
      role="banner"
      sx={{
        position: 'fixed',
        top: { xs: '80px', sm: '90px', md: '100px' }, // Match AppShell content area paddingTop
        left: 0,
        right: 0,
        zIndex: 10002, // Above fixed header (10001) so it's never hidden behind it
        background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
        borderBottom: '2px solid #22c55e',
        boxShadow: '0 4px 16px rgba(34,197,94,0.18)',
        px: { xs: 1.5, sm: 3 },
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      {/* WhatsApp icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <WhatsAppIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          level="body-sm"
          sx={{ fontWeight: 700, color: '#166534', lineHeight: 1.3 }}
        >
          📲 Get weekly predictions &amp; dasha alerts on WhatsApp — free
        </Typography>
        <Typography level="body-xs" sx={{ color: '#15803d', mt: 0.25 }}>
          Sunday forecast · Dasha change alerts · Important transit warnings
        </Typography>
      </Box>

      {/* CTA */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Button
          size="sm"
          variant="solid"
          onClick={handleEnable}
          startDecorator={<WhatsAppIcon sx={{ fontSize: '0.95rem' }} />}
          sx={{
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.78rem',
            px: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #1db954 0%, #0f766e 100%)',
            },
          }}
        >
          Enable alerts
        </Button>
        <IconButton
          size="sm"
          variant="plain"
          onClick={handleDismiss}
          title="Dismiss"
          sx={{ color: '#166534', opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Box>
  );
}
